<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import Avatar from "./Avatar.vue";
import Icon from "./Icon.vue";
import Button from "./Button.vue";
import IconButton from "./IconButton.vue";
import StatusChip from "./StatusChip.vue";
import Switch from "./Switch.vue";
import Select from "./Select.vue";
import Modal from "./Modal.vue";
import SearchField from "./SearchField.vue";
import CrmPanel from "./CrmPanel.vue";
import ContextCards from "./ContextCards.vue";
import ContactAppointments from "./agenda/ContactAppointments.vue";
import ActiveMessagesCard from "./ActiveMessagesCard.vue";
import {
  contactsApi,
  type ContactDetail,
  type ContactMediaItem,
} from "../api/contacts";
import { errorText } from "../api/errors";
import { api, apiUrl } from "../api/client";
import type { ConversationDetail, Outcome } from "../api/types";
import { toast } from "../composables/feedback";
import { localized, t } from "../i18n";
import { closingRuleText, stepWhen } from "../composables/nextSteps";
import { useEscapeLayer } from "../composables/escape";
import { fold } from "../composables/settingsIndex";
import { metaMoney } from "../composables/metaPricing";
import { fxRateText } from "../composables/fx";
import { profileTone } from "../composables/profileColors";
import { can } from "../composables/permissions";
import { costsApi, money, type ConversationCosts } from "../api/costs";
import { vTooltip } from "../directives/tooltip";
const props = defineProps<{
  slug: string;
  contactId: string;
  conversation?: ConversationDetail | null;
  outcomes: Outcome[];
}>();
const emit = defineEmits<{
  close: [];
  open: [conversationId: string];
  /** The person's data was erased on their request; the lists refresh. */
  erased: [];
}>();
const data = ref<ContactDetail | null>(null);
const mode = ref<"info" | "media">("info");
const mediaTab = ref<"media" | "docs" | "links">("media");
useEscapeLayer(
  () => mode.value === "media",
  () => (mode.value = "info"),
);
const mediaItems = ref<ContactMediaItem[]>([]);
const mediaCounts = ref({ media: 0, docs: 0, links: 0 });
async function loadMedia() {
  try {
    const r = await contactsApi.media(props.slug, props.contactId);
    mediaItems.value = r.items;
    mediaCounts.value = r.counts;
  } catch {
    mediaItems.value = [];
  }
}
const mediaTotal = computed(
  () =>
    mediaCounts.value.media + mediaCounts.value.docs + mediaCounts.value.links,
);
const thumbs = computed(() =>
  mediaItems.value.filter((m) => m.kind === "image").slice(0, 3),
);
const shown = computed(() =>
  mediaItems.value.filter((m) => m.group === mediaTab.value),
);
const bytes = (n: number | null | undefined) =>
  !n
    ? ""
    : n < 1024 * 1024
      ? `${Math.round(n / 1024)} KB`
      : `${(n / 1024 / 1024).toFixed(1)} MB`;
const openItem = (m: ContactMediaItem) =>
  window.open(m.url, "_blank", "noopener");
const busy = ref(false);
const editingName = ref(false);
const name = ref("");
const fields = ref<Record<string, string>>({});
const dirty = ref(false);
const nameInput = ref<HTMLInputElement>();
const none = "—";
const yes = t("common.yes");
const no = t("common.no");
function setField(key: string, value: string) {
  fields.value[key] = value;
  dirty.value = true;
}
async function load() {
  data.value = null;
  fieldQuery.value = "";
  dirty.value = false;
  mode.value = "info";
  void loadMedia();
  try {
    data.value = await contactsApi.get(props.slug, props.contactId);
    name.value = data.value.contact.displayName;
    const next: Record<string, string> = {};
    for (const def of data.value.fieldDefinitions) {
      const v = data.value.contact.fields[def.key];
      next[def.key] = v === undefined || v === null ? "" : String(v);
    }
    fields.value = next;
  } catch (err) {
    toast(errorText(err));
  }
}
async function save(patch: Parameters<typeof contactsApi.patch>[2]) {
  busy.value = true;
  try {
    await contactsApi.patch(props.slug, props.contactId, patch);
    toast(t("settings.saved"));
    await load();
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
/** Escape leaves the name as it was; the input's blur then finds nothing to save. */
function cancelName() {
  name.value = data.value?.contact.displayName ?? name.value;
  editingName.value = false;
}
useEscapeLayer(editingName, cancelName);
function saveName() {
  editingName.value = false;
  const next = name.value.trim();
  if (!next || next === data.value?.contact.displayName) return;
  void save({ displayName: next });
}
function saveFields() {
  const defs = data.value?.fieldDefinitions ?? [];
  const out: Record<string, unknown> = {};
  for (const def of defs) {
    const raw = fields.value[def.key] ?? "";
    if (raw === "") {
      out[def.key] = null;
      continue;
    }
    out[def.key] =
      def.type === "number"
        ? Number(raw)
        : def.type === "boolean"
          ? raw === "true"
          : raw;
  }
  void save({ fields: out });
}
const blocked = computed({
  get: () => data.value?.contact.isBlocked ?? false,
  set: (v: boolean) =>
    void save({ isBlocked: v, blockedReason: v ? "manual" : null }),
});
/** LGPD: the person's data as a file, or erased after an explicit confirmation. */
const erasing = ref(false);
const eraseConfirmed = ref(false);
function exportData() {
  window.location.assign(
    apiUrl(`/v1/c/${props.slug}/contacts/${props.contactId}/data-export`),
  );
}
async function eraseData() {
  busy.value = true;
  try {
    await api(`/v1/c/${props.slug}/contacts/${props.contactId}/erase`, {
      method: "POST",
      body: { confirm: true },
    });
    toast(t("contact.erase.done"));
    erasing.value = false;
    emit("erased");
    emit("close");
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
const conv = computed(() => props.conversation ?? null);
/** What this conversation cost (managers and above): Meta by category, AI, the contact's year. */
const costs = ref<ConversationCosts | null>(null);
watch(
  () => [conv.value?.id, conv.value?.metaCost?.micros] as const,
  async ([id]) => {
    costs.value = null;
    if (!id || !can(props.slug, "costs.read")) return;
    costs.value = await costsApi.conversation(props.slug, id).catch(() => null);
  },
  { immediate: true },
);
const costTotal = computed(() => {
  const c = costs.value;
  if (!c) return null;
  const ai = c.ai.brlMicros ?? 0;
  return c.currency === "BRL" || !c.currency ? c.metaMicros + ai : c.metaMicros;
});
const phone = computed(() => {
  const a =
    data.value?.contact.address ?? props.conversation?.contact.address ?? null;
  return a ? `+${a}` : "";
});
/** The phone as shown (+55…), to paste anywhere (Leo, 2026-10-27). */
async function copyPhone() {
  try {
    await navigator.clipboard.writeText(phone.value);
    toast(t("contact.phone_copied"));
  } catch {
    toast(t("common.error"));
  }
}
const history = computed(() =>
  (data.value?.conversations ?? []).filter(
    (c) => c.id !== props.conversation?.id,
  ),
);
function outcomeLabel(code: string | null): string {
  if (!code) return "";
  return localized(props.outcomes.find((o) => o.code === code)?.label) || code;
}
function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
/** Many fields: a search by name, key, option or the value filled in (Leo, 2026-10-26). */
const fieldQuery = ref("");
const shownFields = computed(() => {
  const defs = data.value?.fieldDefinitions ?? [];
  const q = fold(fieldQuery.value.trim());
  if (!q) return defs;
  return defs.filter((def) =>
    fold(
      [
        localized(def.label),
        def.key,
        ...(Array.isArray(def.options) ? (def.options as string[]) : []),
        fields.value[def.key] ?? "",
      ].join(" "),
    ).includes(q),
  );
});
function fieldLabel(def: ContactDetail["fieldDefinitions"][number]): string {
  return (
    (localized(def.label) || def.key) + (def.ai_extractable ? " · IA" : "")
  );
}
watch(() => props.contactId, load);
onMounted(load);
</script>
<template>
  <aside class="detail-panel contact-panel" :aria-label="t('contact.panel')">
    <header class="panel-header">
      <IconButton
        :icon="mode === 'media' ? 'back' : 'close'"
        :label="mode === 'media' ? t('common.back') : t('common.close')"
        @click="mode === 'media' ? (mode = 'info') : emit('close')"
      />
      <h2>{{ mode === "media" ? t("contact.media") : t("contact.panel") }}</h2>
    </header>
    <div v-if="mode === 'media'" class="scroll detail-body">
      <div class="detail-tabs" role="tablist">
        <button
          v-for="tab in ['media', 'docs', 'links'] as const"
          :key="tab"
          type="button"
          role="tab"
          :aria-selected="mediaTab === tab"
          :class="{ active: mediaTab === tab }"
          @click="mediaTab = tab"
        >
          {{ t(("contact.media.tab." + tab) as never) }}
          <small v-if="mediaCounts[tab]" class="muted">
            {{ mediaCounts[tab] }}</small
          >
        </button>
      </div>
      <div
        v-if="shown.length"
        :class="mediaTab === 'media' ? 'media-grid' : 'stack'"
        style="padding: 20px"
      >
        <template v-for="m in shown" :key="m.id">
          <button
            v-if="mediaTab === 'media'"
            type="button"
            v-tooltip="m.caption"
            @click="openItem(m)"
          >
            <img
              v-if="m.kind === 'image'"
              :src="m.url"
              :alt="m.caption ?? ''"
              loading="lazy"
            />
            <video
              v-else-if="m.kind === 'video'"
              :src="m.url"
              muted
              preload="metadata"
            />
            <Icon
              v-else
              :name="m.kind === 'audio' ? 'mic' : 'sticker'"
              :size="32"
            />
          </button>
          <a
            v-else-if="mediaTab === 'docs'"
            class="file-message"
            :href="m.url + '?download=1'"
            :download="m.filename ?? undefined"
          >
            <span class="file-icon"><Icon name="file" :size="26" /></span>
            <span class="grow"
              ><strong>{{ m.filename ?? m.mime }}</strong
              ><small
                >{{ bytes(m.sizeBytes) }} ·
                {{ new Date(m.createdAt).toLocaleDateString() }}</small
              ></span
            >
            <Icon name="download" :size="18" />
          </a>
          <a
            v-else
            class="contact-link"
            :href="m.url"
            target="_blank"
            rel="noopener"
          >
            <Icon name="link" :size="18" />
            <span class="grow"
              ><strong>{{ m.url }}</strong
              ><small class="muted"
                >{{ new Date(m.createdAt).toLocaleDateString() }} ·
                {{ m.text }}</small
              ></span
            >
          </a>
        </template>
      </div>
      <div v-else class="empty-compact">
        <Icon name="media" :size="40" />
        <p>{{ t("contact.media.empty") }}</p>
      </div>
    </div>
    <div v-else class="scroll detail-body">
      <div class="detail-identity">
        <Avatar
          :name="data?.contact.displayName ?? conversation?.contact.name ?? ''"
          :tone="
            profileTone(
              data?.contact.address ?? conversation?.contact.address,
              data?.contact.id ?? conversation?.contact.id,
            )
          "
          :size="96"
        />
        <div
          class="row"
          style="justify-content: center; width: 100%; margin-top: 14px"
        >
          <input
            v-if="editingName"
            ref="nameInput"
            v-model="name"
            class="inline-input"
            maxlength="200"
            :aria-label="t('contact.name')"
            @keydown.enter="saveName"
            @blur="saveName"
          />
          <h2 v-else>
            {{ data?.contact.displayName ?? conversation?.contact.name ?? "" }}
          </h2>
          <IconButton
            :icon="editingName ? 'check' : 'edit'"
            :label="t('contact.edit_name')"
            :disabled="!data"
            @click="
              editingName
                ? saveName()
                : ((editingName = true), $nextTick(() => nameInput?.focus()))
            "
          />
        </div>
        <div v-if="phone" class="row contact-phone">
          <span class="muted">{{ phone }}</span>
          <IconButton
            icon="copy"
            :size="18"
            :label="t('contact.copy_phone')"
            @click="copyPhone"
          />
        </div>
        <div v-if="data?.tags.length" class="row sg-wrap contact-tags">
          <span
            v-for="tag in data.tags"
            :key="tag.id"
            class="chip"
            :style="
              tag.color
                ? { borderColor: tag.color, color: tag.color }
                : undefined
            "
            >{{ localized(tag.name) }}</span
          >
        </div>
      </div>
      <section v-if="conv" class="detail-section">
        <h3 class="contact-section-title">
          {{ t("contact.this_conversation") }}
        </h3>
        <dl class="contact-facts">
          <div>
            <dt>{{ t("contact.source") }}</dt>
            <dd>
              {{ t(("source." + conv.sourceKind) as never)
              }}<template v-if="conv.sourceLabel">
                · {{ conv.sourceLabel }}</template
              >
            </dd>
          </div>
          <div v-if="conv.sourceKind === 'ad' && conv.sourceDetails?.headline">
            <dt>{{ t("contact.ad") }}</dt>
            <dd>
              <a
                v-if="conv.sourceDetails.url"
                :href="conv.sourceDetails.url"
                target="_blank"
                rel="noopener"
                >{{ conv.sourceDetails.headline }}</a
              ><template v-else>{{ conv.sourceDetails.headline }}</template>
            </dd>
          </div>
          <div v-if="conv.goal">
            <dt>{{ t("contact.goal") }}</dt>
            <dd>{{ conv.goal }}</dd>
          </div>
          <div>
            <dt>{{ t("contact.channel") }}</dt>
            <dd>
              {{ conv.channel.name ?? conv.channel.address ?? "" }}
            </dd>
          </div>
          <div>
            <dt>{{ t("contact.started") }}</dt>
            <dd>{{ dateLabel(conv.createdAt) }}</dd>
          </div>
          <div v-if="conv.nextSteps?.closing">
            <dt>{{ t("inbox.next.rule") }}</dt>
            <dd>
              {{
                closingRuleText(conv.nextSteps.closing, (c) => outcomeLabel(c))
              }}
              <small
                v-if="conv.nextSteps.closing.at"
                class="muted facts-line"
                >{{
                  t("inbox.next.closes_at", {
                    when: stepWhen(conv.nextSteps.closing.at),
                  })
                }}</small
              >
            </dd>
          </div>
        </dl>
        <div v-if="costs" class="contact-costs">
          <div class="row">
            <strong class="grow">{{ t("contact.costs.title") }}</strong>
            <strong>{{ money(costTotal, costs.currency ?? "BRL") }}</strong>
          </div>
          <div
            v-for="l in costs.lines"
            :key="l.bucket"
            class="contact-cost-line"
          >
            <span :class="'cost-pill ' + l.bucket">{{
              t(("contact.costs.bucket." + l.bucket) as never)
            }}</span>
            <small class="muted grow">{{
              l.free
                ? t("contact.costs.messages_free", {
                    count: l.messages,
                    free: l.free,
                  })
                : t("contact.costs.messages", { count: l.messages })
            }}</small>
            <span>{{ money(l.micros, costs.currency ?? "BRL", true) }}</span>
          </div>
          <div v-if="costs.ai.usdMicros" class="contact-cost-line">
            <span class="cost-pill">{{ t("contact.costs.ai") }}</span>
            <small class="muted grow">{{
              metaMoney(costs.ai.usdMicros, "USD", true)
            }}</small>
            <span v-tooltip="fxRateText(costs.fx)">{{
              costs.ai.brlMicros !== null
                ? t("fx.approx", {
                    value: money(costs.ai.brlMicros, "BRL", true),
                  })
                : "—"
            }}</span>
          </div>
          <p v-if="costs.freeTierEndedAt" class="contact-cost-note small">
            {{
              t("contact.costs.quota_over", {
                day: new Date(costs.freeTierEndedAt).toLocaleDateString(
                  undefined,
                  {
                    day: "2-digit",
                    month: "2-digit",
                  },
                ),
              })
            }}
          </p>
          <small class="muted">{{
            t("contact.costs.contact_year", {
              count: costs.contact.conversations,
              value: money(costs.contact.metaMicros, costs.currency ?? "BRL"),
            })
          }}</small>
        </div>
        <div v-if="conv.brief" class="contact-brief">
          <strong class="small">{{ t("brief.title") }}</strong>
          <p>{{ conv.brief }}</p>
        </div>
      </section>
      <section class="detail-section" style="padding-top: 8px">
        <button
          class="list-row detail-section-link"
          type="button"
          @click="mode = 'media'"
        >
          <Icon name="media" /><span class="grow">{{
            t("contact.media")
          }}</span>
          <span class="muted small">{{ mediaTotal }}</span>
          <Icon name="chevron" :size="18" />
        </button>
        <div
          v-if="thumbs.length"
          class="detail-media-thumbs"
          style="padding: 12px 0 0"
        >
          <button
            v-for="m in thumbs"
            :key="m.id"
            type="button"
            @click="openItem(m)"
          >
            <img :src="m.url" :alt="m.caption ?? ''" loading="lazy" />
          </button>
        </div>
      </section>
      <section v-if="data?.fieldDefinitions.length" class="detail-section">
        <h3 class="contact-section-title">{{ t("contact.fields") }}</h3>
        <SearchField
          v-if="data.fieldDefinitions.length > 5"
          v-model="fieldQuery"
          class="contact-fields-search"
          :placeholder="t('fields.search')"
        />
        <p v-if="fieldQuery && !shownFields.length" class="small muted">
          {{ t("fields.search_none", { query: fieldQuery }) }}
        </p>
        <div class="contact-fields">
          <div v-for="def in shownFields" :key="def.key" class="wa-field">
            <span v-if="def.type !== 'select' && def.type !== 'boolean'">{{
              fieldLabel(def)
            }}</span>
            <Select
              v-if="def.type === 'select' && Array.isArray(def.options)"
              :model-value="fields[def.key] || none"
              :label="fieldLabel(def)"
              :options="[none, ...(def.options as string[])]"
              @update:model-value="
                (v) => setField(def.key, v === none ? '' : v)
              "
            />
            <Select
              v-else-if="def.type === 'boolean'"
              :model-value="
                fields[def.key] === 'true'
                  ? yes
                  : fields[def.key] === 'false'
                    ? no
                    : none
              "
              :label="fieldLabel(def)"
              :options="[none, yes, no]"
              @update:model-value="
                (v) =>
                  setField(
                    def.key,
                    v === yes ? 'true' : v === no ? 'false' : '',
                  )
              "
            />
            <input
              v-else
              v-model="fields[def.key]"
              :aria-label="fieldLabel(def)"
              :type="
                def.type === 'number'
                  ? 'number'
                  : def.type === 'date'
                    ? 'date'
                    : 'text'
              "
              @input="dirty = true"
            />
          </div>
        </div>
        <div
          v-if="dirty"
          class="row"
          style="justify-content: flex-end; margin-top: 10px"
        >
          <Button compact :loading="busy" @click="saveFields">{{
            t("common.save")
          }}</Button>
        </div>
      </section>
      <ContactAppointments
        v-if="can(slug, 'appointments.read')"
        :slug="slug"
        :contact-id="contactId"
        :contact-name="
          data?.contact.displayName ?? conversation?.contact.name ?? ''
        "
        :conversation="conversation"
      />
      <section class="detail-section">
        <h3 class="contact-section-title">{{ t("active.title") }}</h3>
        <ActiveMessagesCard
          :slug="slug"
          :contact-id="contactId"
          :can-edit="can(slug, 'contacts.write')"
          @changed="load"
        />
        <Switch
          v-model="blocked"
          :label="t('contact.blocked')"
          :disabled="busy || !data"
        />
      </section>
      <section class="detail-section">
        <h3 class="contact-section-title">{{ t("contact.history") }}</h3>
        <p v-if="!history.length" class="muted small">
          {{ t("contact.no_history") }}
        </p>
        <button
          v-for="c in history"
          :key="c.id"
          class="contact-history-row"
          @click="emit('open', c.id)"
        >
          <span class="grow"
            ><strong>{{ dateLabel(c.created_at) }}</strong
            ><br /><small class="muted">{{
              c.source_kind === "history"
                ? c.imported_from === "copiloto"
                  ? t("contact.history_copiloto")
                  : t("contact.history_app")
                : c.status === "closed"
                  ? outcomeLabel(c.outcome_code)
                  : t(("conversation.status." + c.status) as never)
            }}</small></span
          >
          <StatusChip
            :status="
              c.status === 'closed'
                ? c.is_success
                  ? 'success'
                  : 'closed'
                : c.status
            "
            :label="
              c.status === 'closed'
                ? c.is_success
                  ? t('outcome.success')
                  : t('conversation.status.closed')
                : undefined
            "
          />
        </button>
      </section>
      <CrmPanel :slug="slug" :contact-id="contactId" />
      <ContextCards :slug="slug" :contact-id="contactId" />
      <section class="detail-section">
        <h3 class="contact-section-title">{{ t("contact.privacy") }}</h3>
        <button
          class="list-row detail-section-link"
          type="button"
          :disabled="!data"
          @click="exportData"
        >
          <Icon name="download" /><span class="grow">{{
            t("contact.export_data")
          }}</span>
        </button>
        <button
          class="list-row detail-section-link danger"
          type="button"
          :disabled="!data"
          @click="((erasing = true), (eraseConfirmed = false))"
        >
          <Icon name="trash" /><span class="grow">{{
            t("contact.erase.open")
          }}</span>
        </button>
      </section>
    </div>
    <Modal
      :open="erasing"
      :title="
        t('contact.erase.title', { name: data?.contact.displayName ?? '' })
      "
      @close="erasing = false"
    >
      <div class="modal-form">
        <p class="small">{{ t("contact.erase.lead") }}</p>
        <ul class="small muted contact-erase-list">
          <li>{{ t("contact.erase.goes") }}</li>
          <li>{{ t("contact.erase.stays") }}</li>
        </ul>
        <label class="row small" style="gap: 8px"
          ><input v-model="eraseConfirmed" type="checkbox" />{{
            t("contact.erase.confirm")
          }}</label
        >
      </div>
      <template #footer>
        <span class="grow" />
        <Button variant="secondary" @click="erasing = false">{{
          t("common.cancel")
        }}</Button>
        <Button
          variant="danger"
          :disabled="!eraseConfirmed"
          :loading="busy"
          @click="eraseData"
          >{{ t("contact.erase.submit") }}</Button
        >
      </template>
    </Modal>
  </aside>
</template>
